import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { fetchCollectors } from '../../services/collectorService';

const PageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-lg);
  margin-bottom: var(--space-md);
  padding-bottom: var(--space-md);
  border-bottom: 1px solid var(--color-border);

  @media (max-width: 720px) {
    align-items: flex-start;
    flex-direction: column;
    gap: var(--space-sm);
  }
`;

const Intro = styled.div`
  max-width: 680px;
`;

const Kicker = styled.p`
  margin: 0 0 var(--space-xs);
  color: var(--color-accent);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 0;
  color: var(--color-primary);
  font-family: var(--font-serif);
  font-size: clamp(2.25rem, 4vw, 3.25rem);
  font-weight: var(--font-weight-semibold);
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  max-width: 620px;
  margin: var(--space-xs) 0 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
`;

const Button = styled.button`
  min-height: 42px;
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);

  &:hover:not(:disabled) {
    border-color: var(--color-accent);
    color: var(--color-accent);
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: var(--space-md);
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-surface);

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Stat = styled.div`
  min-height: 122px;
  padding: var(--space-sm) var(--space-md);
  border-right: 1px solid var(--color-border-light);

  &:last-child {
    border-right: 0;
  }

  @media (max-width: 800px) {
    &:nth-child(2) {
      border-right: 0;
    }

    &:nth-child(-n + 2) {
      border-bottom: 1px solid var(--color-border-light);
    }
  }
`;

const StatLabel = styled.span`
  display: block;
  color: var(--color-text-light);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const StatValue = styled.strong`
  display: block;
  margin-top: var(--space-xxs);
  color: var(--color-primary);
  font-family: var(--font-serif);
  font-size: 2.25rem;
  font-weight: var(--font-weight-semibold);
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
`;

const StatNote = styled.span`
  display: block;
  margin-top: var(--space-xxs);
  color: var(--color-text-muted);
  font-size: var(--font-size-xs);
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  margin-bottom: var(--space-sm);

  @media (max-width: 860px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const Filters = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: var(--space-xs);
`;

const SearchInput = styled.input`
  flex: 1 1 280px;
  min-width: 220px;
  height: 46px;
  padding: 0 var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--font-size-sm);

  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
`;

const Select = styled.select`
  height: 46px;
  padding: 0 2.5rem 0 var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--font-size-sm);
  cursor: pointer;

  &:hover {
    border-color: var(--color-accent);
  }

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
`;

const ResultsMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  margin-bottom: var(--space-xs);
  color: var(--color-text-light);
  font-size: var(--font-size-sm);
`;

const ClearButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--color-accent);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);

  &:hover {
    color: var(--color-primary);
  }
`;

const TableFrame = styled.div`
  overflow-x: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-surface);
`;

const Table = styled.table`
  width: 100%;
  min-width: 880px;
  border-collapse: collapse;
`;

const HeadCell = styled.th`
  padding: 14px var(--space-sm);
  border-bottom: 1px solid var(--color-border);
  background: var(--color-primary-lighter);
  color: var(--color-text-light);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.1em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
`;

const Row = styled.tr`
  transition: background-color var(--transition-speed) ease;

  &:hover {
    background: rgba(130, 172, 216, 0.08);
  }

  &:last-child td {
    border-bottom: 0;
  }
`;

const Cell = styled.td`
  padding: var(--space-sm);
  border-bottom: 1px solid var(--color-border-light);
  color: var(--color-text);
  font-size: var(--font-size-sm);
  vertical-align: middle;
`;

const CollectorCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 240px;
`;

const Avatar = styled.div`
  display: grid;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  place-items: center;
  overflow: hidden;
  border: 1px solid rgba(93, 64, 34, 0.18);
  border-radius: 50%;
  background: var(--color-mist);
  color: var(--color-primary);
  font-family: var(--font-serif);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CollectorIdentity = styled.div`
  min-width: 0;
`;

const CollectorLink = styled(Link)`
  display: inline-block;
  color: var(--color-primary);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);

  &:hover {
    color: var(--color-accent);
  }
`;

const SecondaryText = styled.span`
  display: block;
  max-width: 250px;
  margin-top: 2px;
  overflow: hidden;
  color: var(--color-text-light);
  font-size: var(--font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== '$tone',
})`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  border-radius: 999px;
  background: ${(props) => {
    if (props.$tone === 'verified') return 'rgba(4, 120, 87, 0.10)';
    if (props.$tone === 'review') return 'rgba(0, 24, 206, 0.08)';
    if (props.$tone === 'attention') return 'rgba(180, 83, 9, 0.10)';
    return 'rgba(93, 64, 34, 0.07)';
  }};
  color: ${(props) => {
    if (props.$tone === 'verified') return 'var(--color-success)';
    if (props.$tone === 'review') return 'var(--color-accent)';
    if (props.$tone === 'attention') return 'var(--color-warning-dark)';
    return 'var(--color-text-muted)';
  }};
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;

  &::before {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    content: '';
  }
`;

const ActivityValue = styled.strong`
  display: block;
  color: var(--color-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
`;

const ActivityNote = styled.span`
  display: block;
  margin-top: 2px;
  color: var(--color-text-light);
  font-size: var(--font-size-xs);
  font-variant-numeric: tabular-nums;
`;

const EmptyState = styled.div`
  padding: var(--space-xl) var(--space-md);
  text-align: center;
`;

const EmptyTitle = styled.h2`
  margin: 0;
  color: var(--color-primary);
  font-family: var(--font-serif);
  font-size: var(--font-size-xl);
`;

const EmptyCopy = styled.p`
  max-width: 460px;
  margin: var(--space-xs) auto 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const ErrorBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  padding: var(--space-md);
  border: 1px solid rgba(180, 35, 24, 0.32);
  border-radius: var(--border-radius-md);
  background: rgba(180, 35, 24, 0.06);
  color: var(--color-error);
`;

const identityMeta = (collector) => {
  if (collector.identityVerified || collector.identityVerificationStatus === 'verified') {
    return { label: 'Verified', tone: 'verified', category: 'verified' };
  }

  if (collector.identityVerificationStatus === 'processing') {
    return { label: 'In review', tone: 'review', category: 'processing' };
  }

  if (collector.identityVerificationStatus === 'requires_input') {
    return { label: 'Action needed', tone: 'attention', category: 'requires_input' };
  }

  return { label: 'Not started', tone: 'muted', category: 'not_started' };
};

const initialsFor = (name) => {
  const initials = String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  return initials || 'CE';
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const formatCurrency = (pence) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format((Number(pence) || 0) / 100);

const Collectors = () => {
  const [collectors, setCollectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [identityFilter, setIdentityFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  const loadCollectors = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      setCollectors(await fetchCollectors());
    } catch (loadError) {
      setError(loadError.message || 'Unable to load collectors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCollectors();
  }, [loadCollectors]);

  const summary = useMemo(() => ({
    verified: collectors.filter((collector) => identityMeta(collector).category === 'verified').length,
    engaged: collectors.filter((collector) => collector.activity.conversations > 0).length,
    purchasers: collectors.filter((collector) => collector.activity.purchases > 0).length,
  }), [collectors]);

  const filteredCollectors = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();
    const filtered = collectors.filter((collector) => {
      const identity = identityMeta(collector);
      const searchMatches =
        !normalizedTerm ||
        collector.displayName.toLowerCase().includes(normalizedTerm) ||
        collector.email.toLowerCase().includes(normalizedTerm) ||
        collector.country.toLowerCase().includes(normalizedTerm);

      if (!searchMatches) return false;
      if (identityFilter !== 'all' && identity.category !== identityFilter) return false;
      if (activityFilter === 'contacted' && collector.activity.conversations === 0) return false;
      if (activityFilter === 'purchased' && collector.activity.purchases === 0) return false;
      if (
        activityFilter === 'inactive' &&
        (collector.activity.conversations > 0 || collector.activity.purchases > 0)
      ) return false;
      return true;
    });

    return filtered.sort((a, b) => {
      if (sortBy === 'name') {
        return a.displayName.localeCompare(b.displayName);
      }
      if (sortBy === 'newest') {
        return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
      }
      return (
        new Date(b.activity.lastActiveAt || b.createdAt || 0).getTime() -
        new Date(a.activity.lastActiveAt || a.createdAt || 0).getTime()
      );
    });
  }, [activityFilter, collectors, identityFilter, searchTerm, sortBy]);

  const filtersActive =
    searchTerm.trim() ||
    identityFilter !== 'all' ||
    activityFilter !== 'all' ||
    sortBy !== 'recent';

  const clearFilters = () => {
    setSearchTerm('');
    setIdentityFilter('all');
    setActivityFilter('all');
    setSortBy('recent');
  };

  return (
    <PageContainer>
      <PageHeader>
        <Intro>
          <Kicker>Collector accounts</Kicker>
          <Title>Collectors</Title>
          <Subtitle>
            Explore who has joined, where they are in identity verification, and how they
            are engaging with the marketplace.
          </Subtitle>
        </Intro>
        <Button type="button" onClick={loadCollectors} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh data'}
        </Button>
      </PageHeader>

      <StatGrid aria-label="Collector summary">
        <Stat>
          <StatLabel>Total collectors</StatLabel>
          <StatValue>{collectors.length}</StatValue>
          <StatNote>Profiles created</StatNote>
        </Stat>
        <Stat>
          <StatLabel>Identity verified</StatLabel>
          <StatValue>{summary.verified}</StatValue>
          <StatNote>Ready to enquire</StatNote>
        </Stat>
        <Stat>
          <StatLabel>Started a conversation</StatLabel>
          <StatValue>{summary.engaged}</StatValue>
          <StatNote>At least one enquiry</StatNote>
        </Stat>
        <Stat>
          <StatLabel>Collectors who bought</StatLabel>
          <StatValue>{summary.purchasers}</StatValue>
          <StatNote>At least one purchase</StatNote>
        </Stat>
      </StatGrid>

      <Controls>
        <Filters>
          <SearchInput
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search name, email, or country"
            aria-label="Search collectors"
          />
          <Select
            value={identityFilter}
            onChange={(event) => setIdentityFilter(event.target.value)}
            aria-label="Filter collectors by identity verification"
          >
            <option value="all">All verification</option>
            <option value="verified">Verified</option>
            <option value="processing">In review</option>
            <option value="requires_input">Action needed</option>
            <option value="not_started">Not started</option>
          </Select>
          <Select
            value={activityFilter}
            onChange={(event) => setActivityFilter(event.target.value)}
            aria-label="Filter collectors by activity"
          >
            <option value="all">All activity</option>
            <option value="contacted">Has enquired</option>
            <option value="purchased">Has purchased</option>
            <option value="inactive">No activity yet</option>
          </Select>
          <Select
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
            aria-label="Sort collectors"
          >
            <option value="recent">Recently active</option>
            <option value="newest">Newest first</option>
            <option value="name">Name A–Z</option>
          </Select>
        </Filters>
      </Controls>

      {error ? (
        <ErrorBox role="alert">
          <span>{error}</span>
          <Button type="button" onClick={loadCollectors}>Try again</Button>
        </ErrorBox>
      ) : (
        <>
          <ResultsMeta>
            <span>
              {loading
                ? 'Loading collectors…'
                : `${filteredCollectors.length} of ${collectors.length} collectors`}
            </span>
            {filtersActive && (
              <ClearButton type="button" onClick={clearFilters}>Clear filters</ClearButton>
            )}
          </ResultsMeta>

          <TableFrame aria-busy={loading}>
            {loading ? (
              <EmptyState>
                <EmptyTitle>Loading collector profiles…</EmptyTitle>
                <EmptyCopy>Gathering account and marketplace activity.</EmptyCopy>
              </EmptyState>
            ) : collectors.length === 0 ? (
              <EmptyState>
                <EmptyTitle>No collectors yet</EmptyTitle>
                <EmptyCopy>New collector profiles will appear here after registration.</EmptyCopy>
              </EmptyState>
            ) : filteredCollectors.length === 0 ? (
              <EmptyState>
                <EmptyTitle>No matching collectors</EmptyTitle>
                <EmptyCopy>Try widening the search or clearing one of the filters.</EmptyCopy>
              </EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <HeadCell scope="col">Collector</HeadCell>
                    <HeadCell scope="col">Identity</HeadCell>
                    <HeadCell scope="col">Joined</HeadCell>
                    <HeadCell scope="col">Marketplace activity</HeadCell>
                    <HeadCell scope="col">Last active</HeadCell>
                  </tr>
                </thead>
                <tbody>
                  {filteredCollectors.map((collector) => {
                    const identity = identityMeta(collector);
                    const conversationLabel =
                      collector.activity.conversations === 1 ? 'conversation' : 'conversations';
                    const purchaseLabel =
                      collector.activity.purchases === 1 ? 'purchase' : 'purchases';

                    return (
                      <Row key={collector.id}>
                        <Cell>
                          <CollectorCell>
                            <Avatar aria-hidden="true">
                              {collector.profileImage ? (
                                <AvatarImage src={collector.profileImage} alt="" />
                              ) : initialsFor(collector.displayName)}
                            </Avatar>
                            <CollectorIdentity>
                              <CollectorLink to={`/collectors/${collector.id}`}>
                                {collector.displayName}
                              </CollectorLink>
                              <SecondaryText>
                                {collector.email || collector.country || 'No email available'}
                              </SecondaryText>
                            </CollectorIdentity>
                          </CollectorCell>
                        </Cell>
                        <Cell>
                          <StatusBadge $tone={identity.tone}>{identity.label}</StatusBadge>
                        </Cell>
                        <Cell>
                          <ActivityValue>{formatDate(collector.createdAt)}</ActivityValue>
                          <ActivityNote>{collector.country || 'Location not provided'}</ActivityNote>
                        </Cell>
                        <Cell>
                          <ActivityValue>
                            {collector.activity.conversations} {conversationLabel}
                          </ActivityValue>
                          <ActivityNote>
                            {collector.activity.purchases} {purchaseLabel}
                            {collector.activity.purchases > 0
                              ? ` · ${formatCurrency(collector.activity.totalSpentPence)}`
                              : ''}
                          </ActivityNote>
                        </Cell>
                        <Cell>
                          <ActivityValue>{formatDate(collector.activity.lastActiveAt)}</ActivityValue>
                          <ActivityNote>
                            {collector.activity.lastActiveAt ? 'Latest marketplace activity' : 'No activity yet'}
                          </ActivityNote>
                        </Cell>
                      </Row>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </TableFrame>
        </>
      )}
    </PageContainer>
  );
};

export default Collectors;
